import { faMemo } from '@fortawesome/pro-solid-svg-icons';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { Tag } from '@growtherapy/sprout-ui';

import { IconMenuItem } from '../../components';
import { useToggleClientInformationDrawerCallback } from '../client-information';
import { hasAnyNewFormsAtom } from '../client-information/state';
import { useTrackClientInfoClicked } from '../client-information/useTrackClientInfoClicked';
import { PUSH_DRAWER_ARIA_CONTROLS_ID } from '../constants';
import { drawerStateAtom, inSessionPatientInformationAtom } from '../state';
import { getIsClientInfoDrawerOpen } from '../utils';

export function ClientInformationDrawerControlMenuItem({
  isClientInfoDrawerOpen,
  hasNewClientInfo,
  onClick,
}: {
  isClientInfoDrawerOpen: boolean;
  hasNewClientInfo?: boolean;
  onClick: () => void;
}) {
  return (
    <IconMenuItem
      aria-controls={PUSH_DRAWER_ARIA_CONTROLS_ID}
      aria-expanded={isClientInfoDrawerOpen}
      onClick={onClick}
      icon={faMemo}
    >
      <span className='sr-only'>{isClientInfoDrawerOpen ? 'Close' : 'Open'} client information</span>
      <span aria-hidden>Client info</span>
      {hasNewClientInfo && (
        <Tag aria-label='new info available' use='lilac'>
          New
        </Tag>
      )}
    </IconMenuItem>
  );
}

export function ClientInformationDrawerControlMenuItemWrapper() {
  const isClientInfoDrawerOpen = getIsClientInfoDrawerOpen(useAtomValue(drawerStateAtom));
  const toggleClientInformationDrawer = useToggleClientInformationDrawerCallback();
  const inSessionClientInformation = useAtomValue(inSessionPatientInformationAtom);
  const trackClientInfoClicked = useTrackClientInfoClicked(inSessionClientInformation?.patientShortId);
  const onClick = useCallback(() => {
    if (!inSessionClientInformation) {
      return;
    }
    const isOpening = !isClientInfoDrawerOpen;
    toggleClientInformationDrawer({
      visitorUuid: inSessionClientInformation.visitorUuid,
      patientShortId: inSessionClientInformation.patientShortId,
    });
    if (isOpening) {
      void trackClientInfoClicked('overflow_menu');
    }
  }, [inSessionClientInformation, isClientInfoDrawerOpen, toggleClientInformationDrawer, trackClientInfoClicked]);
  const hasNewForms = useAtomValue(hasAnyNewFormsAtom);

  return (
    <ClientInformationDrawerControlMenuItem
      isClientInfoDrawerOpen={isClientInfoDrawerOpen}
      hasNewClientInfo={hasNewForms}
      onClick={onClick}
    />
  );
}
